import { useState } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { IconCalendar } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat } from 'app/lib/date';
import { festivalCalendarDate } from 'app/lib/festival-date';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FestivalDateEditor } from './festival-date-editor';

export function FestivalDateField({
  value,
  onChange,
  error,
}: {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  const { t } = useTranslation('festival');
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <Column gap="spacing.general.x2" marginBottom="spacing.general.x4">
      <Text variant="bodyBaseMedium">{t('nextEdition')}</Text>
      <Text variant="bodySmall" color="text.secondary">
        {t('changeLater')}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('startDate')}
        onPress={() => setOpen(true)}
        testID="festival-date-field"
      >
        <Row
          backgroundColor="surface.primary"
          borderRadius="radius.sm"
          padding="spacing.general.x4"
          alignItems="center"
          gap="spacing.general.x4"
        >
          <Column flex={1} gap="spacing.general.x2">
            <Text variant="bodySmallMedium">{t('startDate')}</Text>
            <Text variant="bodySmall" color={value ? 'text.primary' : 'text.tertiary'}>
              {value ? localeAwareFormat(festivalCalendarDate(value), 'd MMMM yyyy') : t('chooseDate')}
            </Text>
          </Column>
          <IconCalendar size={30} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
        </Row>
      </Pressable>
      {error ? (
        <Text color="text.error" accessibilityRole="alert">
          {error}
        </Text>
      ) : null}
      {open ? (
        <FestivalDateEditor
          value={value}
          onClose={() => setOpen(false)}
          onSave={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      ) : null}
    </Column>
  );
}
